import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FilterPopover from '../../community/residents/components/FilterPopover.jsx';
import { relativeTimeFromCampusLocal, campusLocalToMs } from '../../../libs.jsx';

function fullDateTime(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString();
}

// ---------------------------------------------------------------------------
// Status popover — wraps a cell value and shows detail on hover
// ---------------------------------------------------------------------------
const StatusPopover = ({ children, message }) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);
  const hoverTimeout = useRef(null);

  if (!message) return children;

  const handleEnter = () => {
    clearTimeout(hoverTimeout.current);
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popoverW = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    let left = centerX - popoverW / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));

    setStyle({
      position: 'fixed',
      zIndex: 9999,
      left: `${left}px`,
      top: spaceBelow < 100 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 100 ? `${window.innerHeight - rect.top + 6}px` : undefined,
      width: `${popoverW}px`,
      whiteSpace: 'normal',
    });
    setOpen(true);
  };

  const handleLeave = () => {
    hoverTimeout.current = setTimeout(() => setOpen(false), 100);
  };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  return (
    <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={handleLeave} style={{ position: 'relative', display: 'inline-block' }}>
      {children}
      {open ? (
        <span className="na-type-popover" style={style}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{message}</span>
        </span>
      ) : null}
    </span>
  );
};

/**
 * Column definitions for the system devices (repeaters) table (7 columns).
 */
export const SYSTEM_COLUMN_DEFS = [
  { key: 'device_name', label: 'Name', sortable: true, filterable: true },
  { key: 'model_name', label: 'Model', sortable: true, filterable: true },
  { key: 'manufacture_uuid', label: 'Serial Number', sortable: true, filterable: true },
  { key: 'area_name', label: 'Area', sortable: true, filterable: true },
  { key: 'network_id', label: 'Network', sortable: true, filterable: true },
  { key: 'last_active', label: 'Last Active', sortable: true, filterable: false },
  { key: 'mac_addr', label: 'Last Known Server', sortable: true, filterable: true },
  { key: 'is_active', label: 'Active Status', sortable: true, filterable: true },
];

/**
 * SystemDevicesTable — full data table for system devices (repeaters) with
 * sorting, filtering, sticky header, row selection, and row actions.
 */
const SystemDevicesTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onDeleteDevice,
  deleteRights,
  tzOffset = 0,
}) => {
  const activeColumns = SYSTEM_COLUMN_DEFS.filter((col) =>
    visibleColumns.includes(col.key)
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = (columnKey) => {
    const values = new Set();
    (allData || data).forEach((row) => {
      const val = row[columnKey];
      values.add(val != null && val !== '' ? String(val) : '(empty)');
    });
    return [...values].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  const showCheckbox = deleteRights;
  const allSelected =
    data.length > 0 && data.every((r) => selectedIds.includes(r.device_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg
          className="dp-icon-sm dp-sort-icon dp-sort-icon--neutral"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
          />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.5 15.75l7.5-7.5 7.5 7.5"
        />
      </svg>
    ) : (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'device_name':
        return (
          <span
            className="dp-cell-text"
            style={{ fontWeight: 'var(--font-weight-semibold)' }}
          >
            {row.device_name || '\u2014'}
          </span>
        );

      case 'area_name':
        return row.area_id ? (
          <Link to={'/community/areas/' + row.area_id} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>
            {row.area_name}
          </Link>
        ) : (
          <span className="dp-cell-text">{row.area_name || '—'}</span>
        );

      case 'last_active':
        return row.last_active ? (
          <StatusPopover message={fullDateTime(row.last_active)}>
            <span className={`dp-cell-text dp-cell-text--secondary${row?.last_active ? " has-popover" : ""}`}>
              {relativeTimeFromCampusLocal(row.last_active, tzOffset)}
            </span>
          </StatusPopover>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      case 'is_active': {
        if (!row.is_active) {
          return (
            <span className="dp-status dp-status--inactive">
              <span className="dp-status__dot" aria-hidden="true" />
              Unassigned
            </span>
          );
        }
        const stale = row.last_active
          ? (Date.now() - campusLocalToMs(row.last_active, tzOffset)) > 30 * 3600000
          : true;
        return (
          <StatusPopover message={stale ? 'Device may be stale \u2014 test the device to ensure functionality' : undefined}>
            <span className={`dp-status ${stale ? 'dp-status--warning' : 'dp-status--active'}`}>
              <span className="dp-status__dot" aria-hidden="true" />
              Active
            </span>
          </StatusPopover>
        );
      }

      default:
        return (
          <span className="dp-cell-text">{row[columnKey] || '\u2014'}</span>
        );
    }
  };

  return (
    <div className="dp-table-wrap">
      <table className="dp-table">
        <thead className="dp-thead">
          <tr>
            {/* Checkbox column */}
            {showCheckbox ? (
              <th scope="col" className="dp-th dp-th--checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={onSelectAll}
                  className="dp-checkbox"
                  aria-label="Select all devices"
                />
              </th>
            ) : null}

            {/* Actions column */}
            <th scope="col" className="dp-th dp-th--actions">Edit</th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="dp-th">
                <div className="dp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="dp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.filterKey || col.key}
                      label={col.label}
                      values={getFilterValues(col.filterKey || col.key)}
                      selectedValues={filters[col.filterKey || col.key] || []}
                      onChange={onFilterChange}
                      data={allData}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="dp-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.device_id);
            return (
              <tr
                key={row.device_id}
                className={`dp-tr ${isSelected ? 'is-selected' : ''}`}
              >
                {/* Checkbox */}
                {showCheckbox ? (
                  <td className="dp-td dp-td--checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectRow(row.device_id)}
                      className="dp-checkbox"
                      aria-label={`Select ${row.device_name}`}
                    />
                  </td>
                ) : null}

                {/* Row actions kebab */}
                <td className="dp-td dp-td--actions">
                  <SystemRowActionsMenu
                    device={row}
                    deleteRights={deleteRights}
                    onDeleteDevice={onDeleteDevice}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="dp-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * SystemRowActionsMenu — kebab menu for per-row system device actions.
 * Only supports Delete (no unassign for system devices).
 */
const SystemRowActionsMenu = ({
  device,
  deleteRights,
  onDeleteDevice,
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  if (!deleteRights) {
    return null;
  }

  const handleToggle = () => {
    if (!open && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < 180) {
        setMenuStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, right: `${window.innerWidth - rect.right}px`, top: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
      } else {
        setMenuStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, right: `${window.innerWidth - rect.right}px`, bottom: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
      }
    }
    setOpen(!open);
  };

  return (
    <div className="dp-row-actions" ref={menuRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="dp-row-actions__trigger"
        aria-label={`Actions for ${device.device_name}`}
        aria-expanded={open}
      >
        <i className="fe fe-more-vertical" aria-hidden="true" />
      </button>

      {open ? (
        <div className="dp-row-actions__menu" style={menuStyle}>
          <button
            type="button"
            onClick={() => {
              onDeleteDevice(device);
              setOpen(false);
            }}
            className="dp-row-actions__item dp-row-actions__item--danger"
          >
            <svg
              className="dp-icon-md dp-row-actions__item-icon"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
              />
            </svg>
            Delete Device
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default SystemDevicesTable;
